import { inject, Injectable } from '@angular/core';
import { AssetType } from '@app/core/model/entities/asset/asset-type';
import { FieldConfig, FormNode } from '@app/core/model/other/field-config';
import { ColumnGroup, GridConfig, UpdateGridConfigInput } from '@app/core/model/other/grid-config';
import { IIndicatorWidget } from '@app/shared/components/indicator-status-panel/indicator-status-panel.component';
import { getValue, removeTypenameProperty } from '@app/shared/extra/utils';
import { ColDefContext, ColumnBuilder } from '@app/shared/grid/column-builder';
import { FilterKind } from '@app/shared/grid/datasource-utils';
import ApiService from '@services/api.service';
import { AccessManager } from '@services/managers/access.manager';
import { AppManager } from '@services/managers/app.manager';
import { ColDef, ColGroupDef, ColumnState, IRowNode, ValueGetterFunc, ValueGetterParams } from 'ag-grid-community';
import { gql } from 'apollo-angular';
import { plainToInstance } from 'class-transformer';
import { DocumentNode } from 'graphql';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface DatagridBuilder {
  columnDefs: ColDef[],
  toolPanelColDefs: ColGroupDef[],
  defaultColumnStates: ColumnState[],
  indicators: IIndicatorWidget[]
}

@Injectable()
export class GridConfigService {

  private readonly gridConfigGraphQlFragment: DocumentNode;

  private readonly appManager = inject(AppManager);
  private readonly apiService = inject(ApiService);
  private readonly accessManager = inject(AccessManager);
  private readonly columnBuilder = inject(ColumnBuilder);
  private readonly deviceDetectorService = inject(DeviceDetectorService);

  constructor() {
    this.gridConfigGraphQlFragment = gql`
      fragment GridConfigInfo on GridConfig {
        id
        code
        entityType
        columnGroupsList {
          code
          order
          label
          entityType
          customOptions
          assetTypesList {
            id
            name
          }
          allAssetTypes
          columnsList {
            fieldCode
            order
            customOptions
            field {
              code
              label
              tooltip
              entityType
              fieldType
              computed
              parentPathList
              fieldValuesList
              validatorsList {
                conditionsList {
                  field
                  operator
                  value
                }
                definition
                type
              }
            }
            conditionsToViewList {
              field
              operator
              value
            }
            conditionsToEditList {
              field
              operator
              value
            }
          }
        }
        indicatorsList {
          label
          icon
          suffix
          formatType
          aggregation {
            field
            operation
          }
          filtersList {
            kind
            field
            value
            values
            conditions
            textFilter {
              type
              filter
            }
            numberFilter {
              type
              filter
              filterTo
            }
            dateFilter {
              type
              dateFrom
              dateTo
            }
          }
        }
      }
    `;
  }

  /**
   * Fetch and build a GridConfig to be used for displaying a datagrid.
   * The configuration features the column definitions for the datagrid as well as the tool panel and default column
   * states to be applied.
   * @param code GridConfig's code.
   * @param filter Optional filter to discard specific columns.
   * @return An Observable that will emit the GridConfig.
   */
  public getGridConfig(code: string,
                       filter = (_: FieldConfig, __: string[]): boolean => true): Observable<DatagridBuilder> {
    const query = gql`
      query GridConfig($code: String!, $orgId: String!) {
        gridConfig(code: $code, organizationId: $orgId) {
          ...GridConfigInfo
        }
      }
      ${this.gridConfigGraphQlFragment}
    `;
    const variables = {code, orgId: this.appManager.currentOrganization.id};

    return this.apiService.query({query, variables})
      .pipe(
        map(data => {
          return {
            columnGroups: plainToInstance(
              ColumnGroup,
              data['gridConfig']['columnGroupsList'] as ColumnGroup[]
            ),
            indicators: data['gridConfig']['indicatorsList'] as Record<string, any>[]
          };
        }),
        map(gridData => {
          // Extract indicators from each section
          const indicators = this.buildIndicators(gridData.indicators);

          // Ungroup columns, keep column groups' codes and propertiesPaths
          const columnConfigs = gridData.columnGroups.flatMap(columnGroup => {
            return columnGroup.children.map(column => {
              return {
                columnGroupCode: columnGroup.code,
                columnGroupLabel: columnGroup.label,
                assetTypeIds: columnGroup.assetTypes.map(assetType => assetType.id),
                column
              };
            });
          })
            // Filter unwanted columns and columns with no read access
            .filter(({column, assetTypeIds}) => {
              return this.accessManager.hasFieldReadAccess(column.field.code, column.field.entityType)
                && filter(column, assetTypeIds);
            })
            // Reduce columns into gridConfig
            .reduce((gridConfig, {column, columnGroupCode, columnGroupLabel, assetTypeIds}) => {
              // Create column definition
              this.buildColumnDef(column, gridConfig.columnDefs, assetTypeIds);

              // Create tool panel column definition
              this.buildToolPanelColDef(columnGroupCode, columnGroupLabel, column.code, gridConfig.toolPanelColDefs);

              // Create default column state
              this.buildDefaultColumnState(column, gridConfig.defaultColumnStates);

              return gridConfig;
            }, {columnDefs: [], toolPanelColDefs: [], defaultColumnStates: []} as DatagridBuilder);

          return {...columnConfigs, indicators};
        })
      );
  }

  /**
   * Get all GridConfigs of an organization.
   * The gridConfigs are then flattened to calculate the data path for Ag-Grid tree
   * @return Observable that emits a list of FormNodes.
   */
  public getAllFlattenedFormNodes(): Observable<FormNode[]> {
    const query = gql`
      query AllGridConfigs($orgId: String!) {
        gridConfigs(organizationId: $orgId) {
          ...GridConfigInfo
        }
      }
      ${this.gridConfigGraphQlFragment}
    `;
    const variables = {
      orgId: this.appManager.currentOrganization.id
    };
    return this.apiService.query({query, variables})
      .pipe(
        map(data => plainToInstance(GridConfig, data['gridConfigs'] as GridConfig[])),
        map(gridConfigs => this.flattenChildrenRecursively(gridConfigs))
      );
  }

  /**
   * Updates a gridConfig given an input of a few properties
   * @param gridConfigId Id of the gridConfig to update
   * @param gridConfigInput GridConfig data to update
   * @return Observable containing the newly updated gridConfig with its updated column group
   */
  public updateGridConfig(gridConfigId: string, gridConfigInput: UpdateGridConfigInput): Observable<FormNode[]> {
    const mutation = gql`
      mutation UpdateGridConfig($updateGridConfigInput: UpdateGridConfigInput!) {
        updateGridConfig(gridConfigInput: $updateGridConfigInput) {
          ...GridConfigInfo
        }
      }
      ${this.gridConfigGraphQlFragment}
    `;
    const variables = {
      updateGridConfigInput: {id: gridConfigId, ...gridConfigInput}
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(GridConfig, data['updateGridConfig'] as GridConfig)),
        map(forms => this.flattenChildrenRecursively([forms]))
      );
  }

  /**
   * Deletes a form node from the grid config datagrid
   * @param nodeToDelete the Ag-Grid row node to remove along with its children
   * @return Observable containing response if the operation was successful
   */
  public deleteGridFormNodes(nodeToDelete: IRowNode): Observable<FormNode[]> {

    const mutation = gql`
      mutation DeleteFormNodes($nodePath: [String]!, $organizationId: String!) {
        deleteGridConfigFormNodes(organizationId: $organizationId, nodePath: $nodePath) {
          ...GridConfigInfo
        }
      }
      ${this.gridConfigGraphQlFragment}
    `;

    const variables = {
      nodePath: nodeToDelete.data.hierarchy,
      organizationId: this.appManager.currentOrganization.id
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(GridConfig, data['deleteGridConfigFormNodes'])),
        map(forms => this.flattenChildrenRecursively([forms]))
      );
  }

  /**
   * Calculate if a column should be hidden based on the field config and the type of device used.
   * @param column FieldConfig corresponding to the column.
   * @return A boolean value indicating whether the column should be hidden.
   * @private
   */
  private shouldHideColumn(column: FieldConfig): boolean {
    return this.deviceDetectorService.isMobile() && !column.customOptions?.displayMobile;
  }

  /**
   * Build a ColDef from a column's FieldConfig then pushes it to the provided list of column configurations.
   * @param column FieldConfig corresponding to the column.
   * @param columnDefs ColDef array to push the new column into.
   * @param assetTypeIds related asset type IDs.
   * @private
   */
  private buildColumnDef(column: FieldConfig, columnDefs: ColDef[], assetTypeIds?: string[]): void {
    columnDefs.push(
      this.columnBuilder.createColumn(
        column.field.fieldType,
        column.field.label,
        GridConfigService.createValueGetter(),
        {
          colId: column.fieldCode,
          hide: this.shouldHideColumn(column),
          pinned: column.customOptions?.pinned ?? false,
          allowedAggFuncs: column.customOptions?.aggFuncs ?? [],
          aggFunc: column.customOptions?.defaultAggFunc ?? null,
          enableValue: column.customOptions?.aggFuncs?.length > 0,
          context: {
            formatType: column.customOptions?.formatType,
            suffix: column.customOptions?.suffixType,
            translate: column.customOptions?.translate ?? false,
            fieldConfig: column,
            assetTypeIds
          }
        }
      )
    );
  }

  /**
   * Build a ColDef from a FieldConfig to be used for the tool panel, then pushes it to the corresponding group's
   * children within the provided list of column group configurations. If a field group does not exist, it is created
   * before adding the column to its children list.
   * @param groupId ID of the group the field belongs to.
   * @param headerName Label for the column group.
   * @param colId ID of the column to create a ColDef for.
   * @param toolPanelColGroupDefs The ColGroupDef array to push the new column into.
   * @private
   */
  private buildToolPanelColDef(
    groupId: string,
    headerName: string,
    colId: string,
    toolPanelColGroupDefs: ColGroupDef[]
  ): void {
    let colGroup = toolPanelColGroupDefs.find(group => group.groupId === groupId);

    // Create group if it does not already exist in the tool panel
    if (!colGroup) {
      colGroup = {groupId, headerName, children: []};
      toolPanelColGroupDefs.push(colGroup);
    }

    // Add field to group
    colGroup.children.push({colId});
  }

  /**
   * Retrieve the default column state from a FieldConfig, if any, and pushes it to the provided list of column states.
   * @param column FieldConfig corresponding to the column.
   * @param defaultColumnStates List of column states to push the new states into.
   * @private
   */
  private buildDefaultColumnState(column: FieldConfig, defaultColumnStates: ColumnState[]): void {
    if (column.customOptions?.defaultColumnState) {
      defaultColumnStates.push({
        colId: column.fieldCode,
        ...column.customOptions.defaultColumnState
      });
    }
  }

  /**
   * Builds an indicator widget for each of the indicator configurations provided.
   * @param indicatorsData List of indicator configurations.
   * @return List of indicator widgets.
   */
  private buildIndicators(indicatorsData: Record<string, any>[] = []): IIndicatorWidget[] {
    return indicatorsData?.map(config => {
      return {
        label: config.label,
        icon: config.icon,
        formatType: config.formatType,
        suffix: config.suffix,

        aggregation: {
          field: config.aggregation.field,
          operation: config.aggregation.operation,
        },
        filters: config.filtersList.map(f => {
          return {
            kind: f.kind,
            field: f.field,
            value: f.value,
            values: f.values,
            conditions: f.conditions,
            ...(
              f.textFilter != null && f.kind === FilterKind.TEXT ?
                {
                  textFilter: {
                    type: f.textFilter.type,
                    filter: f.textFilter.filter
                  }
                }
                : {}
            ),
            ...(
              f.numberFilter != null && f.kind === FilterKind.NUMBER ?
                {
                  numberFilter: {
                    type: f.numberFilter.type,
                    filter: f.numberFilter.filter,
                    filterTo: f.numberFilter.filterTo
                  }
                }
                : {}
            ),
            ...(
              f.dateFilter != null && f.kind === FilterKind.DATE ?
                {
                  dateFilter: {
                    type: f.dateFilter.type,
                    dateFrom: f.dateFilter.dateFrom,
                    dateTo: f.dateFilter.dateTo
                  }
                }
                : {}
            )
          };
        }) ?? [],

        totalValue: 0,
        filteredValue: 0
      };
    }) ?? [];
  }

  /**
   * Common value getter to retrieve the value in an entity.
   * @private
   */
  private static createValueGetter(): ValueGetterFunc {
    return (params: ValueGetterParams): any => {
      const context = params.colDef.context as ColDefContext;
      return getValue(params.data, context.fieldConfig.fieldPath);
    };
  }

  /**
   * Flattens the children of a form node recursively and creates a list of FormNodes
   * containing the data path from the root FormNode to all children
   * @param data the form nodes to flatten
   * @param parent the parent node, if present
   * @param childHierarchy the hierarchy data structure to build
   * @return a list of FormNodes (gridConfigs, columnGroups, columns) containing the hierarchy path from the root FormNode to all children
   */
  private flattenChildrenRecursively(data?: FormNode[], parent = null, childHierarchy = null): FormNode[] {
    let newData = [];
    if (!data) return newData; // Abort early if data is undefined

    data.forEach(initialRow => {
      let parentHierarchy = [];
      initialRow.hierarchy = parentHierarchy;

      if (parent) {
        initialRow['parent'] = parent;
        parentHierarchy = [...childHierarchy];
        initialRow.hierarchy = parentHierarchy;
      }

      parentHierarchy.push(initialRow['code']);
      newData.push(initialRow);
      switch (initialRow['graphQlTypeName']) {
        case 'GridConfig':
        case 'ColumnGroup':
          newData = [
            ...newData,
            ...this.flattenChildrenRecursively(initialRow['children'], initialRow, parentHierarchy)
          ];
          break;
        default:
          break;
      }
    });
    return newData;
  }

  /**
   * Converts a GridConfig object to an UpdateGridConfigInput object. Only keeps the relevant properties to send downstream.
   * @param gridConfig the GridConfig object to convert
   * @return the GridConfigInput object
   */
  public dataToGridConfigInput(gridConfig: GridConfig): UpdateGridConfigInput {
    return removeTypenameProperty({
      customOptions: gridConfig.customOptions,
      columnGroups: gridConfig.children.map(group => ({
        code: group.code,
        label: group.label,
        order: group.order,
        entityType: group.entityType,
        customOptions: group.customOptions,
        assetTypeIds: group.assetTypes.map((type: AssetType) => type.id) ?? null,
        allAssetTypes: group.allAssetTypes,
        columns: group.children.map(config => ({
          fieldCode: config.fieldCode,
          order: config.order,
          customOptions: config.customOptions,
          conditionsToView: config.conditionsToView,
          conditionsToEdit: config.conditionsToEdit
        }))
      }))
    });
  }

}
